/**
 * Permission Context Provider
 *
 * Manages user permissions globally across the application
 * Provides permission checking functions to all child components
 *
 * Integrates with Keycloak JWT tokens where permissions are at:
 * resource_access.ibill.roles
 * 
 * NOTE: Permissions are stored only in React context (in-memory),
 * not in localStorage, for security reasons.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  extractPermissionsFromToken,
  extractUserInfoFromToken,
  isTokenExpired,
} from '../utils/jwtUtils';

const PermissionContext = createContext(null);

/**
 * Permission Provider Component
 *
 * Wrap your app with this provider to enable permission management
 *
 * @example
 * <PermissionProvider>
 *   <App />
 * </PermissionProvider>
 */
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const hasAttemptedLoad = React.useRef(false);

  /**
   * Load user permissions from JWT token
   * Permissions are kept in memory only (not localStorage)
   */
  const loadPermissions = useCallback(() => {
    try {
      const accessToken = localStorage.getItem('ibill_access_token');

      if (accessToken && !isTokenExpired(accessToken)) {
        const extractedPermissions = extractPermissionsFromToken(accessToken);
        setPermissions(extractedPermissions);

        const userInfo = extractUserInfoFromToken(accessToken);
        setUser(userInfo);
      } else {
        setPermissions([]);
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
      setUser(null);
    } finally {
      setLoading(false);
      hasAttemptedLoad.current = true;
    }
  }, []);

  /**
   * Load permissions on mount
   */
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Listen for storage events (token changes from other tabs)
   * and custom events for same-tab token updates
   */
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'ibill_access_token') {
        loadPermissions();
      }
    };

    const handleTokenUpdate = () => {
      loadPermissions();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('ibill_token_updated', handleTokenUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('ibill_token_updated', handleTokenUpdate);
    };
  }, [loadPermissions]);

  /**
   * Update permissions (useful after login or role change)
   */
  const updatePermissions = useCallback((newPermissions) => {
    setPermissions(newPermissions);
  }, []);

  /**
   * Clear permissions (useful on logout)
   */
  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setUser(null);
  }, []);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (!permission) return true; // No permission required
    return permissions.includes(permission);
  }, [permissions]);

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    return permissionList.some(permission => permissions.includes(permission));
  }, [permissions]);

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    return permissionList.every(permission => permissions.includes(permission));
  }, [permissions]);

  /**
   * Check if user has NONE of the specified permissions
   */
  const hasNonePermissions = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    return permissionList.every(permission => !permissions.includes(permission));
  }, [permissions]);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const value = useMemo(() => ({
    permissions,
    user,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasNonePermissions,
    updatePermissions,
    clearPermissions,
    loadPermissions,
  }), [
    permissions,
    user,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasNonePermissions,
    updatePermissions,
    clearPermissions,
    loadPermissions,
  ]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access permission context
 *
 * @example
 * const { hasPermission, permissions } = usePermissionContext();
 * if (hasPermission('ZONE_CREATE')) {
 *   // Show create button
 * }
 */
export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
